import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./InfoTooltip.scss";

/**
 * InfoTooltip
 * Popover que se activa al hacer hover sobre `trigger` (igual que el
 * componente Atomsv2/Tooltip) pero usa `createPortal` hacia `document.body`
 * para evitar quedar detrás de otros elementos con su propio stacking context
 * (p.ej. cards hermanas). Se anima en dos etapas:
 *  1) Crece horizontalmente hacia la derecha revelando el header (icono + título).
 *  2) Crece verticalmente hacia abajo revelando el contenido (children).
 */
const InfoTooltip = ({ trigger, icon, title, children, className = "", triggerStyle }) => {
    const [visible, setVisible] = useState(false);
    const [stage, setStage] = useState("closed"); // closed | enter | width | full
    const [pos, setPos] = useState({ top: 0, left: 0, ready: false });
    const triggerRef = useRef(null);
    const boxRef = useRef(null);
    const contentInnerRef = useRef(null);
    const hideTimer = useRef(null);

    const clearHide = () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
    const scheduleHide = () => {
        hideTimer.current = setTimeout(() => {
            setVisible(false);
            setStage("closed");
        }, 150);
    };

    const show = () => {
        clearHide();
        // Si ya está visible, solo cancela el cierre pendiente: no reinicia
        // la animación (evita el reinicio constante al mover el mouse entre
        // los elementos internos del popover).
        if (visible) return;
        setPos(p => ({ ...p, ready: false }));
        setVisible(true);
        setStage("enter");
    };

    // Calcula la posición (fixed, relativa al viewport) apenas se monta el popover.
    // Posiciona el popover a la DERECHA del trigger (estilo Azure).
    // Si no cabe a la derecha, se abre a la IZQUIERDA.
    // Verticalmente siempre queda ANCLADO al top del trigger; si el contenido
    // sobresale por abajo, se acota su alto y se vuelve scrolleable.
    useLayoutEffect(() => {
        if (!visible || stage !== "enter" || !triggerRef.current || !boxRef.current) return;

        const t = triggerRef.current.getBoundingClientRect();
        const box = boxRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gap = 8;
        const popoverWidth = 260;

        // Altura total estimada: header (ya renderizado) + contenido (scrollHeight
        // reporta el alto real aunque esté colapsado con grid-template-rows:0fr).
        const headerHeight = box.height;
        const contentHeight = contentInnerRef.current ? contentInnerRef.current.scrollHeight : 0;
        const fullHeight = headerHeight + contentHeight + 10;

        // Horizontal: derecha por defecto, izquierda como fallback.
        const spaceRight = vw - t.right - gap;
        const spaceLeft  = t.left - gap;
        const openRight  = spaceRight >= popoverWidth || spaceRight >= spaceLeft;
        const left = openRight
            ? t.right + gap
            : Math.max(gap, t.left - popoverWidth - gap);

        // Vertical: SIEMPRE anclado al top del trigger.
        // Si el contenido sobresale del viewport, se acota y se hace scrolleable —
        // NUNCA se reposiciona el tooltip lejos del trigger.
        const top = Math.max(gap, t.top);
        const availableHeight = vh - top - gap;
        const needsScroll = fullHeight > availableHeight;
        const contentMaxHeight = needsScroll
            ? Math.max(60, availableHeight - headerHeight - 10)
            : null;

        setPos({ top, left, ready: true, maxContentHeight: contentMaxHeight });

        // Doble rAF para forzar el frame inicial (width:0) antes de animar a stage "width".
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(() => setStage("width"));
        });
        return () => cancelAnimationFrame(raf1);
    }, [visible, stage]);

    // Segunda etapa: tras crecer el ancho, revela el contenido hacia abajo.
    useEffect(() => {
        if (stage !== "width") return;
        const timer = setTimeout(() => setStage("full"), 220);
        return () => clearTimeout(timer);
    }, [stage]);

    useEffect(() => () => clearHide(), []);

    return (
        <div
            className={`InfoTooltip ${className}`}
            style={triggerStyle}
            ref={triggerRef}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
        >
            {trigger}
            {visible && createPortal(
                <div
                    className={`info-tooltip-box stage-${stage}${pos.ready ? " ready" : ""}`}
                    ref={boxRef}
                    style={{ top: pos.top, left: pos.left }}
                    onMouseEnter={(e) => { e.stopPropagation(); clearHide(); }}
                    onMouseLeave={(e) => { e.stopPropagation(); scheduleHide(); }}
                >
                    {(icon || title) && (
                        <div className="info-tooltip-header">
                            {icon && <span className="icon info-tooltip-icon" data-icon={icon} />}
                            {title && <span className="info-tooltip-title">{title}</span>}
                        </div>
                    )}
                    <div className="info-tooltip-content">
                        <div
                            className="info-tooltip-content-inner"
                            ref={contentInnerRef}
                            style={pos.maxContentHeight ? { maxHeight: pos.maxContentHeight, overflowY: "auto" } : undefined}
                        >
                            {children}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default InfoTooltip;
